import { Button, Modal, ModalDialog, Stack, Typography } from "@mui/joy";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import addAgentNoteSchema, { AddAgentNoteFormType } from "@/schemas/agents/addAgentNote";
import { createNote } from "@/services/api/agents/notes";
import Select from "@/components/FormComponents/Select";
import DateInput from "@/components/FormComponents/DateInput";
import Textarea from "@/components/FormComponents/Textarea";
import { AGENT_NOTE_TYPES } from "../constants";

type AddNoteModalProps = {
  open: boolean;
  onClose: () => void;
  email: string;
};

function AddNoteModal({ open, onClose, email }: AddNoteModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviewedOnAsRequired, setReviewedOnAsRequired] = useState(false);
  const queryClient = useQueryClient();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
  } = useForm<AddAgentNoteFormType>({
    resolver: zodResolver(addAgentNoteSchema),
    defaultValues: {
      note: "",
      type: "",
      reviewed: "true",
      reviewOn: null,
    },
  });

  const onSubmit = async (data: AddAgentNoteFormType) => {
    try {
      setIsSubmitting(true);

      const payload = {
        toEmail: email,
        comment: data.note,
        type: data.type,
        reviewed: data.reviewed === "true",
        reviewOn: data.reviewOn || null,
      };

      await createNote(payload);

      reset();
      onClose();

      // Invalidate agent notes query to refresh the list
      queryClient.invalidateQueries({ queryKey: ["agentNotes", email] });

      toast.success("Note added successfully");
    } catch (err: any) {
      const res = err.response?.data;
      toast.error(res?.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    reset();
    clearErrors();
  };

  const typeOptions = Object.entries(AGENT_NOTE_TYPES).map(([key, value]) => ({
    label: value,
    value: key,
  }));

  const reviewedOptions = [
    { label: "Yes", value: "false" },
    { label: "No", value: "true" },
  ];

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ minWidth: 500, maxHeight: "90vh", display: "flex", flexDirection: "column", p: 0 }}>
        <Typography level="h3" sx={{ px: 3, pt: 3, pb: 1 }}>
          Add New Note
        </Typography>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
        >
          <Stack gap={2} sx={{ px: 3, pb: 2, overflowY: "auto", flex: 1 }}>
            <Textarea
              name="note"
              label="Note"
              placeholder="Enter your note here..."
              register={register}
              error={errors.note?.message}
              required
            />

            <Select
              name="type"
              label="Type"
              placeholder="Select type"
              control={control}
              items={typeOptions}
              error={errors.type?.message}
              required
            />

            <Select
              name="reviewed"
              label="Review"
              placeholder="Select reviewed status"
              control={control}
              items={reviewedOptions}
              error={errors.reviewed?.message}
              onValueChange={(val) =>
                val === "false" ? setReviewedOnAsRequired(true) : setReviewedOnAsRequired(false)
              }
              required
            />

            <DateInput
              name="reviewOn"
              label="Reviewed On"
              placeholder="Select review date"
              control={control}
              error={errors.reviewOn?.message}
              required={reviewedOnAsRequired}
            />
          </Stack>

          <Stack
            direction="row"
            gap={2}
            justifyContent="flex-end"
            sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}
          >
            <Button type="button" variant="outlined" color="neutral" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              Add
            </Button>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

export default AddNoteModal;
